import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", code: "", address: "", phone: "", reg_no: "", nabh_no: "", logo_url: "" };

export default function AdminBranches() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => { const { data } = await api.get("/hospital/branches"); setItems(data.items); };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || "", code: b.code || "", address: b.address || "",
      phone: b.phone || "", reg_no: b.reg_no || "", nabh_no: b.nabh_no || "",
      logo_url: b.logo_url || "",
    });
    setOpen(true);
  };
  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/hospital/branches/${editing.id}`, form);
        toast.success("Branch updated");
      } else {
        await api.post("/hospital/branches", form);
        toast.success("Branch created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/hospital/branches/${confirmDel.id}`);
      toast.success("Branch deleted");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="section-label">Administration</div>
          <h1 className="font-heading text-3xl font-semibold">Branches</h1>
          <p className="mt-1 text-sm text-ink-muted">{items.length} branch{items.length === 1 ? "" : "es"}</p>
        </div>
        <Button onClick={openCreate} className="bg-brand hover:bg-brand-700" data-testid="branches-new-btn"><Plus size={14} className="mr-2" />Add branch</Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-4 py-3">Logo</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Address</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Reg / NABH</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map(b => (
              <tr key={b.id} className="hover:bg-secondary/30" data-testid={`branch-row-${b.code}`}>
                <td className="px-4 py-3">
                  {b.logo_url
                    ? <img src={b.logo_url} alt={b.name} className="h-8 w-8 rounded object-contain bg-white border border-border" />
                    : <div className="grid h-8 w-8 place-items-center rounded bg-brand/10 text-[10px] font-semibold text-brand">{(b.code || "?").slice(0, 2)}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{b.code}</td>
                <td className="px-4 py-3 font-medium">{b.name}</td>
                <td className="px-4 py-3 text-xs text-ink-muted">{b.address || "—"}</td>
                <td className="px-4 py-3 text-xs">{b.phone || "—"}</td>
                <td className="px-4 py-3 text-xs text-ink-muted">
                  <div>Reg: {b.reg_no || "—"}</div>
                  <div>NABH: {b.nabh_no || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(b)} data-testid={`branch-edit-${b.code}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDel(b)} data-testid={`branch-delete-${b.code}`}><Trash2 size={14} className="text-rose-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-ink-muted">No branches yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit branch" : "New branch"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Branch name</Label><Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="branch-name-input" /></div>
            <div><Label>Code</Label><Input value={form.code} onChange={e => set("code", e.target.value.toUpperCase())} placeholder="e.g. JAY" data-testid="branch-code-input" /></div>
            <div>
              <Label>Branch logo (JPG or PNG · auto-resized to fit OPD card)</Label>
              <div className="mt-2 flex items-center gap-3">
                {form.logo_url
                  ? <img src={form.logo_url} alt="logo" className="h-14 w-14 rounded border border-border object-contain bg-white" data-testid="branch-logo-preview" />
                  : <div className="grid h-14 w-14 place-items-center rounded border border-border bg-secondary text-[10px] font-semibold text-ink-muted">No logo</div>}
                <Input
                  type="file" accept="image/jpeg,image/png" data-testid="branch-logo-file"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const fd = new FormData(); fd.append("file", f);
                    try {
                      const tid = editing?.id ? `&target_id=${editing.id}` : "";
                      const { data } = await api.post(`/files/upload-logo?scope=branch${tid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      set("logo_url", data.url);
                      toast.success(`Logo uploaded (${data.width}×${data.height})`);
                    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
                  }}
                />
                {form.logo_url && (
                  <Button
                    type="button" variant="outline" size="sm"
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200"
                    onClick={() => set("logo_url", "")}
                    data-testid="branch-logo-remove"
                    title="Remove logo (saves on next 'Save changes')"
                  >
                    Remove logo
                  </Button>
                )}
              </div>
              <div className="mt-1 text-[11px] text-ink-muted">Max 5 MB · auto-resized to 256×256 max while keeping aspect ratio. Click <b>Remove logo</b> to clear the current logo, then Save.</div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={e => set("address", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => set("phone", e.target.value)} /></div>
              <div><Label>Registration no</Label><Input value={form.reg_no} onChange={e => set("reg_no", e.target.value)} /></div>
            </div>
            <div><Label>NABH no</Label><Input value={form.nabh_no} onChange={e => set("nabh_no", e.target.value)} /></div>
            <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="branch-save-btn">{editing ? "Save changes" : "Create branch"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-rose-700">Delete this branch?</DialogTitle></DialogHeader>
          <p className="text-sm">Soft-delete <b>{confirmDel?.name}</b> ({confirmDel?.code})? Existing records remain in DB for audit/recovery but the branch is hidden from listings. You cannot delete the last active branch of a hospital.</p>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button onClick={remove} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="branch-delete-confirm">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
